import type { Difficulty } from './types'

export type RevealTiming = {
  startSec: number
  fadeInSec: number
  endSec: number
}

export type NoticeTiming = RevealTiming & {
  holdSec: number
}

export type DefeatOverlayTimingTable = {
  deathTipFresh: { startSec: number; fadeInSec: number }
  deathTipRepeat: { startSec: number; fadeInSec: number }
  difficultyHint: { startSec: number; fadeInSec: number }
  fragmentNotice: { startSec: number; fadeInSec: number; holdSec: number }
  gapBeforeRetrySec: number
  retryButtonFadeInSec: number
  earlyInputBufferSec: number
  freshDeathMax: number
  difficultyHintDeathMin: number
  devScaleCycle: readonly number[]
}

// commit 5349776 の fresh/repeat/withNotice 固定ゲートを、表示要素から計算する時刻表へ移した。
// 描画と入力は必ず defeatOverlayTimeline() の同じ結果を使い、「出た＝押せる」を一致させる。
//
// 🔴 2026-07-31 実機裁定 (ユーザー):
//   ① 全体を ×1.5 で確定。下の値はその倍率を焼き込んだ後の数字 (DEV の ×1.0 = これ)。
//   ② 「再演がまだ見えていないのに押せている」違和感を消すため、
//      **ゲートはボタンが出始めた時刻ではなく、出切った時刻**にする (下の gateSec)。
//      あわせて「一拍の間」をボタンのフェードインそのものへ畳んだ (gapBeforeRetrySec=0)。
//      この付け替えでゲート秒数は1ミリも変わらない = ユーザーが良しとした体感を保ったまま、
//      見えている物と押せる物だけが一致する。
export const DEFEAT_OVERLAY_TIMING_SEC: DefeatOverlayTimingTable = {
  deathTipFresh: { startSec: 0.750, fadeInSec: 0.900 },
  deathTipRepeat: { startSec: 0.375, fadeInSec: 0.675 },
  difficultyHint: { startSec: 1.050, fadeInSec: 0.900 },
  fragmentNotice: { startSec: 0.600, fadeInSec: 0.375, holdSec: 3.300 },
  // 間はボタンのフェードインが担う。ここを足すと「見えない待ち」が戻る。
  gapBeforeRetrySec: 0,
  retryButtonFadeInSec: 0.375,
  earlyInputBufferSec: 0.375,
  freshDeathMax: 3,
  difficultyHintDeathMin: 3,
  devScaleCycle: [0.5, 0.75, 1.0, 1.5, 2.0],
}

export type DefeatOverlayContext = {
  deaths: number
  showDifficultyHint: boolean
  hasNotice: boolean
}

export type DefeatOverlayTimeline = {
  deathTip: RevealTiming
  difficultyHint: RevealTiming | null
  fragmentNotice: NoticeTiming | null
  retryButton: RevealTiming
  gateSec: number
  inputBufferOpenSec: number
}

function reveal(startSec: number, fadeInSec: number, scale: number): RevealTiming {
  const start = startSec * scale
  const fadeIn = fadeInSec * scale
  return { startSec: start, fadeInSec: fadeIn, endSec: start + fadeIn }
}

export function defeatDifficultyHintVisible(
  difficulty: Difficulty,
  deaths: number,
  timing = DEFEAT_OVERLAY_TIMING_SEC,
): boolean {
  return difficulty === 'senshuraku' && deaths >= timing.difficultyHintDeathMin
}

export function defeatOverlayTimeline(
  context: DefeatOverlayContext,
  timing = DEFEAT_OVERLAY_TIMING_SEC,
  scale = 1,
): DefeatOverlayTimeline {
  const tipSource = context.deaths <= timing.freshDeathMax
    ? timing.deathTipFresh
    : timing.deathTipRepeat
  const deathTip = reveal(tipSource.startSec, tipSource.fadeInSec, scale)
  const difficultyHint = context.showDifficultyHint
    ? reveal(timing.difficultyHint.startSec, timing.difficultyHint.fadeInSec, scale)
    : null
  const fragmentNoticeReveal = context.hasNotice
    ? reveal(timing.fragmentNotice.startSec, timing.fragmentNotice.fadeInSec, scale)
    : null
  const fragmentNotice = fragmentNoticeReveal
    ? {
      ...fragmentNoticeReveal,
      holdSec: timing.fragmentNotice.holdSec * scale,
      endSec: fragmentNoticeReveal.endSec + timing.fragmentNotice.holdSec * scale,
    }
    : null

  // ゲート式は要素名を知らない。表示対象をこの配列へ足せば、最遅の終端へ自動追従する。
  const activeElements: RevealTiming[] = [deathTip]
  if (difficultyHint) activeElements.push(difficultyHint)
  if (fragmentNotice) activeElements.push(fragmentNotice)
  const latestElementEndSec = Math.max(...activeElements.map((element) => element.endSec))
  const retryButtonFadeInSec = timing.retryButtonFadeInSec * scale
  const retryButton = {
    startSec: latestElementEndSec + timing.gapBeforeRetrySec * scale,
    fadeInSec: retryButtonFadeInSec,
    endSec: latestElementEndSec + timing.gapBeforeRetrySec * scale + retryButtonFadeInSec,
  }
  // 🔴 ゲート = ボタンが **出切った** 時刻。出始めに合わせると「まだ見えていないのに押せる」になる
  //   (2026-07-31 実機でユーザーが指摘した違和感の正体)。
  const gateSec = retryButton.endSec
  const inputBufferOpenSec = Math.max(0, gateSec - timing.earlyInputBufferSec * scale)

  return {
    deathTip,
    difficultyHint,
    fragmentNotice,
    retryButton,
    gateSec,
    inputBufferOpenSec,
  }
}

export function defeatRetryGateSec(
  context: DefeatOverlayContext,
  timing = DEFEAT_OVERLAY_TIMING_SEC,
  scale = 1,
): number {
  return defeatOverlayTimeline(context, timing, scale).gateSec
}
